// Central API client for The Brain Theory backend
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const TOKEN_KEY = 'tbt_token_v1';

export const tokenStore = {
  get: () => localStorage.getItem(TOKEN_KEY),
  set: (t) => localStorage.setItem(TOKEN_KEY, t),
  clear: () => localStorage.removeItem(TOKEN_KEY),
};

async function request(path, { method = 'GET', body, auth = true } = {}) {
  const headers = { 'Content-Type': 'application/json' };
  if (auth) {
    const token = tokenStore.get();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body != null ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    let msg = `Request failed (${res.status})`;
    let detail = null;
    try {
      const data = await res.json();
      detail = data.detail;
      if (typeof detail === 'string') {
        msg = detail;
      } else if (detail && typeof detail === 'object') {
        msg = detail.message || msg;
      } else if (data.message) {
        msg = data.message;
      }
    } catch {}
    const err = new Error(msg);
    err.status = res.status;
    err.detail = detail;
    err.code = (detail && typeof detail === 'object') ? detail.code : null;
    throw err;
  }
  if (res.status === 204) return null;
  return res.json();
}

export const api = {
  // Auth
  signup:  (body) => request('/auth/signup',  { method: 'POST', body, auth: false }),
  login:   (body) => request('/auth/login',   { method: 'POST', body, auth: false }),
  google:  (body) => request('/auth/google',  { method: 'POST', body, auth: false }),
  forgot:  (body) => request('/auth/forgot',  { method: 'POST', body, auth: false }),
  capturePhoneLead: (body) => request('/leads/phone', { method: 'POST', body, auth: false }),
  me:      ()     => request('/auth/me'),
  trialStatus: () => request('/trial/status'),
  changePassword: (body) => request('/auth/password', { method: 'PATCH', body }),

  // Profile / user
  updateMe:       (body) => request('/users/me',                { method: 'PATCH', body }),
  submitOnboarding: (answers) => request('/users/me/onboarding', { method: 'POST', body: { answers } }),
  deleteMe:       ()     => request('/users/me',                { method: 'DELETE' }),
  exportMe:       ()     => request('/users/me/export'),

  // Settings
  updatePreferences:   (body) => request('/users/me/preferences',   { method: 'PATCH', body }),
  updateNotifications: (body) => request('/users/me/notifications', { method: 'PATCH', body }),
  updatePrivacy:       (body) => request('/users/me/privacy',       { method: 'PATCH', body }),
  updateAppearance:    (body) => request('/users/me/appearance',    { method: 'PATCH', body }),
  updateAccessibility: (body) => request('/users/me/accessibility', { method: 'PATCH', body }),

  // Puzzles / sessions
  dailyPuzzles:    () => request('/puzzles/daily'),
  categoryPuzzles: (cat) => request(`/puzzles/category/${encodeURIComponent(cat)}`),
  categories:      () => request('/puzzles/categories'),
  submitSession:   (body) => request('/sessions', { method: 'POST', body }),
  speakAnswer: async (puzzleId, blob) => {
    const fd = new FormData();
    const ext = (blob.type || '').includes('mp4') ? 'mp4' : (blob.type || '').includes('ogg') ? 'ogg' : 'webm';
    fd.append('puzzleId', puzzleId);
    fd.append('audio', blob, `answer.${ext}`);
    const res = await fetch(`${API_BASE}/puzzles/speak`, {
      method: 'POST', body: fd, headers: { Authorization: `Bearer ${tokenStore.get()}` },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const d = data.detail;
      const err = new Error((d && d.message) || (typeof d === 'string' ? d : 'Could not process your recording.'));
      err.code = d && d.code;
      throw err;
    }
    return data;
  },

  // Progress
  progressOverview:     () => request('/progress/overview'),
  progressTrends:       () => request('/progress/trends'),
  progressSkills:       () => request('/progress/skills'),
  progressAchievements: () => request('/progress/achievements'),

  // Leaderboard
  leaderboard: (window = 'weekly', scope = 'global') =>
    request(`/leaderboard?window=${window}&scope=${scope}`),

  // Dashboard
  dashboard: () => request('/dashboard'),

  // Billing
  plans:            () => request('/billing/plans', { auth: false }),
  subscription:     () => request('/billing/subscription'),
  createOrder:      (body) => request('/billing/create-order', { method: 'POST', body }),
  verifyPayment:    (body) => request('/billing/verify-payment', { method: 'POST', body }),
  createShare: (body) => request('/share', { method: 'POST', body }),
  funnelChallenges: () => request('/funnel/challenges', { auth: false }),
  chooseFree: () => request('/billing/choose-free', { method: 'POST' }),
  usageToday: () => request('/usage/today'),
  notifications: () => request('/notifications'),
  readAllNotifications: () => request('/notifications/read-all', { method: 'POST' }),
  createSubscription: (body) => request('/billing/subscriptions/create', { method: 'POST', body }),
  subscriptionStatus: (id) => request(`/billing/subscriptions/status${id ? `?subscription_id=${encodeURIComponent(id)}` : ''}`),
  verifySubscription: (body) => request('/billing/subscriptions/verify', { method: 'POST', body }),
  cancelSubscription: () => request('/billing/cancel',    { method: 'POST' }),
  resumeSubscription: () => request('/billing/resume',    { method: 'POST' }),
};
